import React, { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  Search,
  MoreHorizontal,
  UserPlus,
  Key,
  Shield,
  ShieldCheck,
  ShieldOff,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  ScanBarcode,
  Check,
  X,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POSUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Create User Dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    name: '',
    pin: '',
    role: 'cashier',
    email: ''
  });
  const [showPin, setShowPin] = useState(false);
  
  // Edit Role Dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  
  // Reset PIN Dialog
  const [showResetPinDialog, setShowResetPinDialog] = useState(false);
  const [resetPinUser, setResetPinUser] = useState(null);
  const [newPin, setNewPin] = useState('');
  
  // Delete Confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/pos/auth/users`);
      setUsers(res.data);
    } catch (err) {
      toast.error('Failed to load POS users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.name || !newUser.pin) {
      toast.error('Please fill all required fields');
      return;
    }
    
    if (newUser.pin.length < 4) {
      toast.error('PIN must be at least 4 digits');
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/pos/auth/register`, newUser);
      toast.success(`POS user "${newUser.name}" created successfully`);
      setShowCreateDialog(false);
      setNewUser({ username: '', name: '', pin: '', role: 'cashier', email: '' });
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!editingUser || !newRole) return;
    
    try {
      await axios.put(`${API_URL}/api/pos/auth/users/${editingUser.id}`, null, {
        params: { role: newRole }
      });
      toast.success(`Role updated to ${newRole}`);
      setShowEditDialog(false);
      loadUsers();
    } catch (err) {
      toast.error('Failed to update role');
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await axios.put(`${API_URL}/api/pos/auth/users/${user.id}`, null, {
        params: { is_active: !user.is_active }
      });
      toast.success(user.is_active ? 'User deactivated' : 'User activated');
      loadUsers();
    } catch (err) {
      toast.error('Failed to update user status');
    }
  };

  const handleResetPin = async () => {
    if (!resetPinUser || !newPin || newPin.length < 4) {
      toast.error('PIN must be at least 4 digits');
      return;
    }
    
    try {
      await axios.put(`${API_URL}/api/pos/auth/users/${resetPinUser.id}/reset-pin`, null, {
        params: { new_pin: newPin }
      });
      toast.success('PIN reset successfully');
      setShowResetPinDialog(false);
      setNewPin('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset PIN');
    }
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    
    try {
      await axios.delete(`${API_URL}/api/pos/auth/users/${deletingUser.id}`);
      toast.success('User deactivated');
      setShowDeleteDialog(false);
      loadUsers();
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { label: 'Admin', variant: 'default', icon: ShieldCheck, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
      manager: { label: 'Manager', variant: 'secondary', icon: Shield, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
      cashier: { label: 'Cashier', variant: 'outline', icon: Users, color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' }
    };
    const config = roleConfig[role] || roleConfig.cashier;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    total: users.length,
    active: users.filter(u => u.is_active).length,
    admins: users.filter(u => u.role === 'admin').length,
    managers: users.filter(u => u.role === 'manager').length,
    cashiers: users.filter(u => u.role === 'cashier').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ScanBarcode className="h-7 w-7 text-primary" />
            POS User Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage cashiers, managers, and admins for your POS terminals
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
          <UserPlus className="h-4 w-4" />
          Add POS User
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-sm text-muted-foreground">Total Users</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-emerald-600">{stats.active}</div>
            <p className="text-sm text-muted-foreground">Active</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-purple-600">{stats.admins}</div>
            <p className="text-sm text-muted-foreground">Admins</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.managers}</div>
            <p className="text-sm text-muted-foreground">Managers</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-600">{stats.cashiers}</div>
            <p className="text-sm text-muted-foreground">Cashiers</p>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle>POS Users</CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>
              <Button variant="outline" size="icon" onClick={loadUsers}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No POS users found</p>
              <Button onClick={() => setShowCreateDialog(true)} variant="outline" className="mt-4">
                <UserPlus className="h-4 w-4 mr-2" /> Create First User
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold ${
                          user.role === 'admin' ? 'bg-gradient-to-br from-purple-500 to-pink-500' :
                          user.role === 'manager' ? 'bg-gradient-to-br from-blue-500 to-cyan-500' :
                          'bg-gradient-to-br from-slate-500 to-slate-600'
                        }`}>
                          {user.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="font-medium">{user.name}</p>
                          {user.email && <p className="text-xs text-muted-foreground">{user.email}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{user.username}</TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell>
                      {user.is_active ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 mr-1.5" />
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800">
                          <span className="h-1.5 w-1.5 rounded-full bg-red-500 mr-1.5" />
                          Inactive
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setEditingUser(user);
                            setNewRole(user.role);
                            setShowEditDialog(true);
                          }}>
                            <Edit className="h-4 w-4 mr-2" /> Change Role
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            setResetPinUser(user);
                            setShowResetPinDialog(true);
                          }}>
                            <Key className="h-4 w-4 mr-2" /> Reset PIN
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleToggleStatus(user)}>
                            {user.is_active ? (
                              <><ShieldOff className="h-4 w-4 mr-2" /> Deactivate</>
                            ) : (
                              <><ShieldCheck className="h-4 w-4 mr-2" /> Activate</>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => {
                              setDeletingUser(user);
                              setShowDeleteDialog(true);
                            }}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Role Permissions Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Role Permissions</CardTitle>
          <CardDescription>Understanding what each role can do in the POS system</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 rounded-lg border bg-slate-50 dark:bg-slate-900">
              <div className="flex items-center gap-2 mb-3">
                <Users className="h-5 w-5 text-slate-600" />
                <span className="font-semibold">Cashier</span>
              </div>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500" /> Process sales</li>
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500" /> Open/Close shifts</li>
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500" /> View own transactions</li>
                <li className="flex items-center gap-2"><X className="h-3 w-3 text-red-500" /> Void transactions</li>
                <li className="flex items-center gap-2"><X className="h-3 w-3 text-red-500" /> Access reports</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg border bg-blue-50 dark:bg-blue-950/30">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="h-5 w-5 text-blue-600" />
                <span className="font-semibold">Manager</span>
              </div>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500" /> All Cashier permissions</li>
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500" /> Void transactions</li>
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500" /> View all reports</li>
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500" /> Apply discounts</li>
                <li className="flex items-center gap-2"><X className="h-3 w-3 text-red-500" /> Change settings</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg border bg-purple-50 dark:bg-purple-950/30">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="h-5 w-5 text-purple-600" />
                <span className="font-semibold">Admin</span>
              </div>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500" /> All Manager permissions</li>
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500" /> Full system access</li>
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500" /> Change POS settings</li>
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500" /> Manage terminals</li>
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500" /> User management</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" /> Create POS User
            </DialogTitle>
            <DialogDescription>
              Add a new cashier, manager, or admin to your POS system
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                placeholder="John Smith"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Username *</Label>
              <Input
                placeholder="john_smith"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value.toLowerCase().replace(/\s/g, '_') })}
              />
              <p className="text-xs text-muted-foreground">Used for POS login</p>
            </div>
            <div className="space-y-2">
              <Label>PIN Code * (4-6 digits)</Label>
              <div className="relative">
                <Input
                  type={showPin ? 'text' : 'password'}
                  placeholder="1234"
                  maxLength={6}
                  value={newUser.pin}
                  onChange={(e) => setNewUser({ ...newUser, pin: e.target.value.replace(/\D/g, '') })}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowPin(!showPin)}
                >
                  {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cashier">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" /> Cashier
                    </div>
                  </SelectItem>
                  <SelectItem value="manager">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4" /> Manager
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4" /> Admin
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Email (Optional)</Label>
              <Input
                type="email"
                placeholder="john@example.com"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser} disabled={creating}>
              {creating ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription>
              Update the role for {editingUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cashier">Cashier</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateRole}>
              Update Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset PIN Dialog */}
      <Dialog open={showResetPinDialog} onOpenChange={setShowResetPinDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" /> Reset PIN
            </DialogTitle>
            <DialogDescription>
              Set a new PIN for {resetPinUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New PIN (4-6 digits)</Label>
              <Input
                type="password"
                placeholder="Enter new PIN"
                maxLength={6}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowResetPinDialog(false);
              setNewPin('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleResetPin}>
              Reset PIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate User?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate "{deletingUser?.name}"? They will no longer be able to login to the POS system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default POSUserManagement;
